import { UserContextFeatureFlags, UserContextRbacPermission } from 'core/userContext/types';
import { WorkflowType } from 'modules/domains';
import { NavbarOptionFeatureFlag } from 'modules/nav/types/navigation';
import { hasFeatureFlagEnabled } from 'modules/nav/utils/hasFeatureFlagEnabled';
import { SEARCH_PAGES } from 'modules/quickActionsMenu/constants';
import { NavigationItem, PAGE } from 'modules/quickActionsMenu/types';
import { BADGE_VARIANTS } from 'ui/badge';
import { StatusBadgeVariants } from 'ui/badge/constants';
import { doesRbacPermissionHaveAccess } from 'utils/permissions';

export const getSearchPage = (value: string) =>
  Object.values(SEARCH_PAGES).find((searchPage) => searchPage.type === value);

export const PLACEHOLDER_TEXT = {
  [PAGE.HOME_PAGE]: 'Search items and commands...',
  [PAGE.CAMPAIGN_CREATION]: '',
  [PAGE.CAMPAIGN_MEDIUMS]: 'Select a medium...',
  [PAGE.CAMPAIGN_SEARCH]: 'Search campaigns...',
  [PAGE.JOURNEY_CREATION]: '',
  [PAGE.JOURNEY_SEARCH]: 'Search published journeys...',
  [PAGE.SNIPPET_SEARCH]: 'Search snippets...',
  [PAGE.TEMPLATE_CREATION]: '',
  [PAGE.TEMPLATE_MEDIUMS]: 'Select a medium...',
  [PAGE.TEMPLATE_SEARCH]: 'Search templates...',
  [PAGE.USER_SEARCH]: 'Search users by email or ID...',
};

/** Create search input placeholder text per page */
export const getPlaceholderText = (page?: PAGE) => {
  switch (page) {
    case PAGE.CAMPAIGN_MEDIUMS:
      return PLACEHOLDER_TEXT[PAGE.CAMPAIGN_MEDIUMS];
    case PAGE.CAMPAIGN_SEARCH:
      return PLACEHOLDER_TEXT[PAGE.CAMPAIGN_SEARCH];
    case PAGE.JOURNEY_SEARCH:
      return PLACEHOLDER_TEXT[PAGE.JOURNEY_SEARCH];
    case PAGE.SNIPPET_SEARCH:
      return PLACEHOLDER_TEXT[PAGE.SNIPPET_SEARCH];
    case PAGE.TEMPLATE_CREATION:
      return PLACEHOLDER_TEXT[PAGE.TEMPLATE_CREATION];
    case PAGE.TEMPLATE_MEDIUMS:
      return PLACEHOLDER_TEXT[PAGE.TEMPLATE_MEDIUMS];
    case PAGE.TEMPLATE_SEARCH:
      return PLACEHOLDER_TEXT[PAGE.TEMPLATE_SEARCH];
    case PAGE.USER_SEARCH:
      return PLACEHOLDER_TEXT[PAGE.USER_SEARCH];
    case PAGE.HOME_PAGE:
    default:
      return PLACEHOLDER_TEXT[PAGE.HOME_PAGE];
  }
};

export const BADGE_TEXT = {
  [PAGE.HOME_PAGE]: 'Home',
  [PAGE.CAMPAIGN_SEARCH]: 'Campaigns',
  [PAGE.JOURNEY_SEARCH]: 'Journeys',
  [PAGE.SNIPPET_SEARCH]: 'Snippets',
  [PAGE.TEMPLATE_CREATION]: 'Create template',
  [PAGE.TEMPLATE_MEDIUMS]: 'Templates mediums',
  [PAGE.TEMPLATE_SEARCH]: 'Templates',
  [PAGE.USER_SEARCH]: 'Users',
};

/** Create badge text used in Command Root (hidden text for screen readers) */
export const getBadgeText = (page?: PAGE) => {
  switch (page) {
    case PAGE.CAMPAIGN_SEARCH:
      return BADGE_TEXT[PAGE.CAMPAIGN_SEARCH];
    case PAGE.JOURNEY_SEARCH:
      return BADGE_TEXT[PAGE.JOURNEY_SEARCH];
    case PAGE.SNIPPET_SEARCH:
      return BADGE_TEXT[PAGE.SNIPPET_SEARCH];
    case PAGE.TEMPLATE_CREATION:
      return BADGE_TEXT[PAGE.TEMPLATE_CREATION];
    case PAGE.TEMPLATE_MEDIUMS:
      return BADGE_TEXT[PAGE.TEMPLATE_MEDIUMS];
    case PAGE.TEMPLATE_SEARCH:
      return BADGE_TEXT[PAGE.TEMPLATE_SEARCH];
    case PAGE.USER_SEARCH:
      return BADGE_TEXT[PAGE.USER_SEARCH];
    case PAGE.HOME_PAGE:
    default:
      return BADGE_TEXT[PAGE.HOME_PAGE];
  }
};

// Pulled logic from Journeys module
type GetJourneyStatusParams = {
  isArchived: boolean;
  workflowType: WorkflowType;
  enabled: boolean;
};

type GetJourneyParamsResponse = {
  displayText: string;
  variant: StatusBadgeVariants;
};

export const getJourneyStatus = ({
  isArchived,
  workflowType,
  enabled,
}: GetJourneyStatusParams): GetJourneyParamsResponse => {
  if (isArchived) {
    return { displayText: 'Archived', variant: BADGE_VARIANTS.FINISHED };
  }

  if (workflowType === WorkflowType.DRAFT) {
    return { displayText: 'Draft', variant: BADGE_VARIANTS.DRAFT };
  }

  return enabled
    ? { displayText: 'Live', variant: BADGE_VARIANTS.RUNNING }
    : { displayText: 'Off', variant: BADGE_VARIANTS.ABORTED };
};

export const doesUserHaveAccess = (
  {
    featureFlags: requiredFeatureFlags = [],
    permissions = [],
    requireOnlyOneFlag = false,
  }: {
    featureFlags?: NavbarOptionFeatureFlag[];
    permissions?: UserContextRbacPermission[];
    requireOnlyOneFlag?: boolean;
  },
  userContextRbacPermissions: UserContextRbacPermission[] = [],
  userContextFeatureFlags: UserContextFeatureFlags = {},
) => {
  const hasFeatureFlagsEnabled = hasFeatureFlagEnabled(
    userContextFeatureFlags,
    requiredFeatureFlags,
    requireOnlyOneFlag,
  );
  const hasPermission = doesRbacPermissionHaveAccess(userContextRbacPermissions, permissions);

  return hasFeatureFlagsEnabled && hasPermission;
};

export const defineExternalLink = (
  isExternal: NavigationItem['isExternal'],
  featureFlags: UserContextFeatureFlags,
) => (typeof isExternal === 'function' ? !!isExternal(featureFlags) : !!isExternal);
